import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { EntityTypeEnum, WorkflowActions } from 'api'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { ActionItem, SplitButtonDropdown, SplitButtonType } from 'view/components/menus'
import { TradingEntityDialog } from './TradingEntityDialog'
import { TradingEntityUploadDialog } from 'view/shared/tradingEntityUpload'
import { TradingEntityProductUploadDialog } from 'view/shared/tradingEntityProductUpload'
import { TradingRelationshipUploadDialog } from 'view/shared/tradingRelationshipUpload'
import { canManageTradingEntitiesSelector } from 'selectors/permissionSelectors'
import { CorrelatedResponseCallback, EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { downloadUrl } from 'utils'
import tradingEntityUploadTemplateUrl from './assets/TradingEntityUploadTemplate.xlsx'
import tradingEntityProductUploadTemplateUrl from './assets/TradingEntityProductUploadTemplate.xlsx'
import tradingRelationshipUploadTemplateUrl from './assets/TradingRelationshipUploadTemplate.xlsx'
import { Locale, Translation } from 'locale'

interface TradingEntityToolbarProps extends ModalConsumerProps {
  canManageTradingEntities: boolean
  onClick: (entityTypeEnum: EntityTypeEnum, onCorrelatedResponse?: CorrelatedResponseCallback) => unknown
}

const mapStateToProps = createStructuredSelector({
  canManageTradingEntities: canManageTradingEntitiesSelector
})

const mapDispatchToProps = {
  onClick: WorkflowActions.createNewWorkflow
}

const SplitButtonStyled = styled(SplitButtonDropdown)`
  max-width: 160px;
`

const TradingEntityToolbarComponent: React.FunctionComponent<TradingEntityToolbarProps> = ({
  canManageTradingEntities,
  onClick,
  showModal
}) => {
  if (!canManageTradingEntities) {
    return null
  }

  const handleAddButtonClick = () => {
    const correlatedResponseCallback = (workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
      showModal(
        <TradingEntityDialog tradingEntityEntityId={newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_ENTITY]} workflowId={workflowId} />
      )
    }

    onClick(EntityTypeEnum.TRADING_ENTITY, correlatedResponseCallback)
  }

  const handleUploadTradingEntity = () => {
    showModal(<TradingEntityUploadDialog />)
  }

  const handleUploadTradingEntityProduct = () => {
    showModal(<TradingEntityProductUploadDialog />)
  }

  const handleUploadTradingRelationship = () => {
    showModal(<TradingRelationshipUploadDialog />)
  }

  const handleDownloadTradingEntitySpreadsheet = () => {
    downloadUrl(tradingEntityUploadTemplateUrl, Locale.translate(Translation.TC_DOWNLOAD_TRADING_ENTITY_TEMPLATE_FILENAME))
  }

  const handleDownloadTradingEntityProductSpreadsheet = () => {
    downloadUrl(tradingEntityProductUploadTemplateUrl, Locale.translate(Translation.TC_DOWNLOAD_TRADING_ENTITY_PRODUCT_TEMPLATE_FILENAME))
  }

  const handleDownloadTradingRelationshipSpreadsheet = () => {
    downloadUrl(tradingRelationshipUploadTemplateUrl, Locale.translate(Translation.TC_DOWNLOAD_TRADING_RELATIONSHIP_TEMPLATE_FILENAME))
  }

  const toolbarActions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_BUTTON_NEW_TRADING_ENTITY),
      type: SplitButtonType.Primary,
      onClick: handleAddButtonClick,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_UPLOAD_TRADING_ENTITIES),
      type: SplitButtonType.Primary,
      onClick: handleUploadTradingEntity,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_UPLOAD_TRADING_ENTITY_PRODUCTS),
      type: SplitButtonType.Primary,
      onClick: handleUploadTradingEntityProduct,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_UPLOAD_TRADING_RELATIONSHIPS),
      type: SplitButtonType.Primary,
      onClick: handleUploadTradingRelationship,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DOWNLOAD_TRADING_ENTITIES_SPREADSHEET),
      type: SplitButtonType.Primary,
      onClick: handleDownloadTradingEntitySpreadsheet,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DOWNLOAD_TRADING_ENTITY_PRODUCTS_SPREADSHEET),
      type: SplitButtonType.Primary,
      onClick: handleDownloadTradingEntityProductSpreadsheet,
      enabled: true
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DOWNLOAD_TRADING_RELATIONSHIPS_SPREADSHEET),
      type: SplitButtonType.Primary,
      onClick: handleDownloadTradingRelationshipSpreadsheet,
      enabled: true
    }
  ]

  return <SplitButtonStyled dataTestId={'TradingEntityToolbar-TextButton-ButtonNewTradingEntity'} actions={toolbarActions} maxButtons={1} />
}

export const TradingEntityToolbar = withModal(connect(mapStateToProps, mapDispatchToProps)(TradingEntityToolbarComponent))
